import type { ArtData, CurrencyType } from "./art";
import type Stripe from "stripe";
import { GenericHandlerResponse } from "./handler";
import { PAYMENT_DATA_PARTITIONS } from "./paymentTable";
import { LocationData } from "./location";
import { FromConstObject } from ".";

/**
 * Checkout DB record types
 */
export const CHECKOUT_STATUS = {
  OPEN: "open",
  READY: "ready",
  SUBMITTED: "submitted",
  FINISHED: "finished",
} as const;
export type CheckoutStatus = FromConstObject<typeof CHECKOUT_STATUS>;

export type DoridiumCheckout = {
  id: string;
  object: typeof PAYMENT_DATA_PARTITIONS.DORIDIUM_CHECKOUT;
  items?: ArtData[];
  originalItems?: ArtData[];
  contactEmail?: string;
  deliveryLocation?: LocationData;
  userId?: string;
  cusId?: string;
  payment?: {
    id: string;
    clientSecret?: string;
    subtotal: number;
    total: number;
    taxCalculation?: {
      id: string;
      taxAmount: number;
      taxBreakdown: Stripe.Tax.Calculation.TaxBreakdown[];
    };
    currency: CurrencyType;
  };
  status: CheckoutStatus;
  created: number; //seconds
} & (
  | {
      status: typeof CHECKOUT_STATUS.FINISHED;
      orderSlug: string;
    }
  | {
      status: Omit<CheckoutStatus, typeof CHECKOUT_STATUS.FINISHED>;
      orderSlug?: undefined;
    }
);

/**
 * get checkout handler types
 */
export type GetCheckoutInput = {
  id: string;
  authorization: string;
};

export type GetCheckoutRequest = {
  id: string;
};
export type GetCheckoutResponseData = {
  checkout: PatchCheckoutResponseData["checkout"] & {
    status: DoridiumCheckout["status"];
    orderSlug: DoridiumCheckout["orderSlug"];
  };
};

export type GetCheckoutResponse = GenericHandlerResponse<
  GetCheckoutResponseData,
  GetCheckoutErrorInfoType
>;
/** GetCheckout handler error info types */
export type GetCheckoutErrorInfoType = null | undefined;

/**
 * patch checkout handler types
 */
export type PatchCheckoutRequest = {
  id: string;
  items?: DoridiumCheckout["items"];
  deliveryLocation?: DoridiumCheckout["deliveryLocation"];
  contactEmail?: DoridiumCheckout["contactEmail"];
  userId?: DoridiumCheckout["userId"];
};

export const deliveryLocationRequiredParams = [
  "addressLine",
  "addressee",
  "city",
  "countryCode",
  "state",
  "zip",
] as (keyof NonNullable<PatchCheckoutRequest["deliveryLocation"]>)[];

export type PatchCheckoutResponseData = {
  checkout: {
    id: string;
    items?: DoridiumCheckout["items"];
    contactEmail?: DoridiumCheckout["contactEmail"];
    deliveryLocation?: DoridiumCheckout["deliveryLocation"];
    payment?: DoridiumCheckout["payment"];
  };
};

export type PatchCheckoutResponse = GenericHandlerResponse<
  PatchCheckoutResponseData,
  PatchCheckoutErrorInfoType
>;

/** PatchCheckout handler error info types */

export type PatchCheckoutErrorInfoType =
  | null
  | undefined
  | (
      | {
          problemLocation: "checkoutItems";
          problemItems?: ProblemItemType[];
        }
      | {
          problemLocation: "deliveryLocation";
          checkout?: Partial<PatchCheckoutResponseData["checkout"]>;
          locationError: string;
        }
      | {
          problemLocation: "payment";
          checkout?: Partial<PatchCheckoutResponseData["checkout"]>;
        }
      | {
          problemLocation: "contactEmail";
          reason?: string;
        }
    );

export type ProblemItemType = {
  item: NonNullable<DoridiumCheckout["items"]>[number];
  error: {
    message: string;
    solutions?: ("addToCart" | "removeFromCart")[];
  };
};

/**
 * Post checkout handler types
 */
export type PostCheckoutRequest = {
  id: string;
  items?: DoridiumCheckout["items"];
  userId?: DoridiumCheckout["userId"];
  contactEmail?: DoridiumCheckout["contactEmail"];
  deliveryLocation?: DoridiumCheckout["deliveryLocation"];
};

export type PostCheckoutResponseData = {
  checkout: {
    id: string;
    items?: DoridiumCheckout["items"];
    contactEmail?: DoridiumCheckout["contactEmail"];
    deliveryLocation?: DoridiumCheckout["deliveryLocation"];
    payment?: DoridiumCheckout["payment"];
  };
};

export type PostCheckoutResponse = GenericHandlerResponse<
  PostCheckoutResponseData,
  PostCheckoutErrorInfoType
>;
/** PostCheckout handler error info types */
export type PostCheckoutErrorInfoType = null | undefined;

/**
 * checkout page types
 */
export const CHECKOUT_STEP_OPTIONS = {
  EMAIL: "email",
  LOCATION: "location",
  PAYMENT: "payment",
  STATUS: "status",
} as const;
export type CheckoutStepOptions = FromConstObject<typeof CHECKOUT_STEP_OPTIONS>;
